import { useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
  makeStyles,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import PersonIcon from "@mui/icons-material/Person";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import VisibilityIcon from "@mui/icons-material/Visibility";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";

import { MP } from "../../monitoramento-preco/components/mpConstants";
import { formatBRL } from "../../monitoramento-preco/components/mpUtils";
import { TIPO_COLETA } from "../utils/mapColetaTableRow";
import fetchConcorrenteFoto from "../utils/fetchConcorrenteFoto";

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: MP.radiusLg,
    overflow: "hidden",
    margin: theme.spacing(2),
    width: `calc(100% - ${theme.spacing(4)}px)`,
    maxWidth: 960,
    [theme.breakpoints.up("md")]: {
      maxWidth: 1040,
    },
    [theme.breakpoints.up("lg")]: {
      maxWidth: 1120,
    },
  },
  titleRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    paddingRight: 40,
  },
  content: {
    padding: theme.spacing(2.5, 2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2.5, 3),
    },
  },
  section: {
    marginBottom: theme.spacing(2.5),
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    color: MP.primary,
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "0.8125rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  fieldLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: MP.textMuted,
    marginBottom: 2,
  },
  fieldValue: {
    fontSize: "0.9375rem",
    color: MP.text,
    wordBreak: "break-word",
  },
  priceHighlight: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 12px",
    borderRadius: MP.radiusMd,
    backgroundColor: MP.primarySoft,
    color: MP.primary,
    fontWeight: 700,
    fontSize: "1rem",
  },
  concorrentesGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(1.25),
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
  concorrenteCard: {
    border: `1px solid ${MP.border}`,
    borderRadius: MP.radiusMd,
    padding: theme.spacing(1.5, 1.75),
    backgroundColor: "#fafbfc",
    height: "100%",
    boxSizing: "border-box",
  },
  concorrenteNome: {
    fontWeight: 600,
    fontSize: "0.9375rem",
    color: MP.text,
    marginBottom: 4,
  },
  concorrenteProduto: {
    fontSize: "0.8125rem",
    color: MP.textMuted,
    marginBottom: theme.spacing(1),
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 4,
  },
  fotoBtn: {
    marginTop: theme.spacing(1),
    textTransform: "none",
    fontWeight: 600,
  },
  emptyConcorrentes: {
    padding: theme.spacing(2, 1.75),
    borderRadius: MP.radiusMd,
    border: `1px dashed ${MP.border}`,
    color: MP.textMuted,
    fontSize: "0.875rem",
    textAlign: "center",
  },
  tipoChip: {
    fontWeight: 600,
  },
  fotoPaper: {
    borderRadius: MP.radiusMd,
    margin: theme.spacing(2),
    width: `calc(100% - ${theme.spacing(4)}px)`,
    maxWidth: 720,
  },
  fotoImageWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
    maxHeight: "70vh",
    overflow: "auto",
    backgroundColor: "#f5f7fa",
    borderRadius: MP.radiusMd,
    padding: theme.spacing(1),
  },
  fotoImage: {
    maxWidth: "100%",
    maxHeight: "68vh",
    objectFit: "contain",
    borderRadius: 4,
  },
  fotoLoading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1.5),
    minHeight: 200,
    color: MP.textMuted,
  },
}));

function DetailField({ classes, label, value, xs = 12, sm = 6, md = 6 }) {
  return (
    <Grid item xs={xs} sm={sm} md={md}>
      <Typography className={classes.fieldLabel}>{label}</Typography>
      <Typography className={classes.fieldValue}>{value || "—"}</Typography>
    </Grid>
  );
}

function SectionHeader({ classes, icon: Icon, title }) {
  return (
    <Box className={classes.sectionHeader}>
      <Icon fontSize="small" />
      <Typography className={classes.sectionTitle}>{title}</Typography>
    </Box>
  );
}

function ConcorrenteCard({ classes, concorrente, loadingFoto, onViewFoto }) {
  const {
    nome,
    nomeProduto,
    encontrado,
    precoNormal,
    emPromocao,
    precoPromocional,
    temFoto,
    idProdutoConcorrente,
  } = concorrente;

  return (
    <Box className={classes.concorrenteCard}>
      <Typography className={classes.concorrenteNome}>{nome || "—"}</Typography>
      {nomeProduto ? (
        <Typography className={classes.concorrenteProduto}>
          {nomeProduto}
        </Typography>
      ) : null}
      <Box className={classes.chipRow}>
        <Chip
          size="small"
          label={encontrado ? "Encontrado" : "Não encontrado"}
          style={{
            backgroundColor: encontrado ? "#e8f5e9" : "#fce4ec",
            color: encontrado ? "#2e7d32" : "#c62828",
            fontWeight: 600,
          }}
        />
        {encontrado && precoNormal != null ? (
          <Chip
            size="small"
            icon={<LocalOfferIcon style={{ fontSize: 16 }} />}
            label={`Normal ${formatBRL(precoNormal)}`}
            variant="outlined"
          />
        ) : null}
        {encontrado && emPromocao && precoPromocional != null ? (
          <Chip
            size="small"
            label={`Promo ${formatBRL(precoPromocional)}`}
            style={{
              backgroundColor: "#fff3e0",
              color: "#e65100",
              fontWeight: 600,
            }}
          />
        ) : null}
      </Box>
      {temFoto ? (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          className={classes.fotoBtn}
          startIcon={
            loadingFoto === idProdutoConcorrente ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <VisibilityIcon fontSize="small" />
            )
          }
          disabled={loadingFoto === idProdutoConcorrente}
          onClick={() => onViewFoto(concorrente)}
        >
          Ver foto
        </Button>
      ) : null}
    </Box>
  );
}

function DialogFotoConcorrente({ open, onClose, title, loading, src, error }) {
  const classes = useStyles();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.fotoPaper }}
    >
      <DialogTitle disableTypography>
        <Typography component="h3" variant="h3" style={{ fontWeight: 700 }}>
          {title || "Foto do concorrente"}
        </Typography>
        <IconButton
          aria-label="fechar foto"
          onClick={onClose}
          size="small"
          style={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box className={classes.fotoLoading}>
            <CircularProgress size={36} color="primary" />
            <Typography variant="body2">Carregando foto...</Typography>
          </Box>
        ) : null}
        {!loading && error ? (
          <Typography variant="body2" color="error" align="center">
            {error}
          </Typography>
        ) : null}
        {!loading && !error && src ? (
          <Box className={classes.fotoImageWrap}>
            <Box
              component="img"
              src={src}
              alt={title || "Foto do concorrente"}
              className={classes.fotoImage}
            />
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function DialogDetalheColeta({ open, onClose, coleta }) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const snackbar = useSnackbar();

  const [loadingFotoId, setLoadingFotoId] = useState(null);
  const [fotoModal, setFotoModal] = useState({
    open: false,
    loading: false,
    src: "",
    title: "",
    error: "",
  });

  if (!coleta) return null;

  const dataEnvio = coleta.dataEnvioUtc
    ? moment.utc(coleta.dataEnvioUtc).local().format("DD/MM/YYYY [às] HH:mm")
    : null;

  const concorrentes = coleta.concorrentes ?? [];
  const tipoLabel = TIPO_COLETA[coleta.tipoColeta] ?? coleta.tipoColeta;

  const handleCloseFotoModal = () => {
    setFotoModal({
      open: false,
      loading: false,
      src: "",
      title: "",
      error: "",
    });
    setLoadingFotoId(null);
  };

  const handleViewFoto = async (concorrente) => {
    const idProdutoConcorrente = concorrente.idProdutoConcorrente;
    if (!idProdutoConcorrente || !coleta.idColeta) return;

    const titulo = [concorrente.nome, concorrente.nomeProduto]
      .filter(Boolean)
      .join(" — ");

    setLoadingFotoId(idProdutoConcorrente);
    setFotoModal({
      open: true,
      loading: true,
      src: "",
      title: titulo || "Foto do concorrente",
      error: "",
    });

    try {
      const src = await fetchConcorrenteFoto(
        coleta.idColeta,
        idProdutoConcorrente,
      );
      if (!src) {
        setFotoModal((prev) => ({
          ...prev,
          loading: false,
          error: "Foto não disponível.",
        }));
        return;
      }
      setFotoModal((prev) => ({
        ...prev,
        loading: false,
        src,
      }));
    } catch (error) {
      const msg =
        typeof error === "string"
          ? error
          : error?.message || "Não foi possível carregar a foto.";
      snackbar.showMessage(msg, { severity: "error" });
      setFotoModal((prev) => ({
        ...prev,
        loading: false,
        error: msg,
      }));
    } finally {
      setLoadingFotoId(null);
    }
  };

  const handleCloseMain = () => {
    handleCloseFotoModal();
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseMain}
        fullWidth
        scroll="paper"
        classes={{ paper: classes.paper }}
      >
        <DialogTitle disableTypography style={{ paddingBottom: 8 }}>
          <Box className={classes.titleRow}>
            <Box flex={1} minWidth={0}>
              <Typography
                component="h2"
                variant={isMobile ? "h3" : "h2"}
                style={{ fontWeight: 700 }}
              >
                Coleta #{coleta.idColeta}
              </Typography>
              {dataEnvio ? (
                <Typography variant="caption" style={{ color: MP.textMuted }}>
                  Enviada em {dataEnvio}
                </Typography>
              ) : null}
            </Box>
            {coleta.tipoColeta ? (
              <Chip
                size="small"
                label={tipoLabel}
                style={{
                  backgroundColor:
                    coleta.tipoColeta === "padrao" ? "#e8f5e9" : "#fce4ec",
                  color: coleta.tipoColeta === "padrao" ? "#2e7d32" : "#c62828",
                  fontWeight: 600,
                }}
                className={classes.tipoChip}
              />
            ) : null}
          </Box>
          <IconButton
            aria-label="fechar"
            onClick={handleCloseMain}
            size="small"
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers className={classes.content}>
          <Box className={classes.section}>
            <SectionHeader classes={classes} icon={PersonIcon} title="Coleta" />
            <Grid container spacing={2}>
              <DetailField
                classes={classes}
                label="Representante"
                value={coleta.representante}
                md={6}
              />
              <DetailField
                classes={classes}
                label="Data da coleta"
                value={
                  coleta.dataColeta?.replaceAll?.("-", "/") ?? coleta.dataColeta
                }
                md={6}
              />
            </Grid>
          </Box>

          <Divider style={{ marginBottom: 20 }} />

          <Box className={classes.section}>
            <SectionHeader
              classes={classes}
              icon={StorefrontIcon}
              title="Loja"
            />
            <Grid container spacing={2}>
              <DetailField
                classes={classes}
                label="Razão social"
                value={coleta.loja?.razaoSocial}
                xs={12}
                md={12}
              />
              <DetailField
                classes={classes}
                label="Grupo econômico"
                value={coleta.loja?.grupoEconomico}
                md={6}
              />
              <DetailField
                classes={classes}
                label="Cidade"
                value={coleta.loja?.cidade}
                sm={6}
                md={3}
              />
              <DetailField
                classes={classes}
                label="UF"
                value={coleta.loja?.uf}
                sm={6}
                md={3}
              />
            </Grid>
          </Box>

          <Divider style={{ marginBottom: 20 }} />

          <Box className={classes.section}>
            <SectionHeader
              classes={classes}
              icon={Inventory2Icon}
              title="Produto de referência"
            />
            <Grid container spacing={2}>
              <DetailField
                classes={classes}
                label="Marca"
                value={coleta.produto?.marca}
                sm={6}
                md={3}
              />
              <DetailField
                classes={classes}
                label="SKU"
                value={coleta.produto?.sku}
                sm={6}
                md={3}
              />
              <DetailField
                classes={classes}
                label="Produto"
                value={coleta.produto?.nome}
                sm={6}
                md={5}
              />
              <DetailField
                classes={classes}
                label="Linha"
                value={coleta.produto?.linha}
                sm={6}
                md={4}
              />
              <DetailField
                classes={classes}
                label="Categoria"
                value={coleta.produto?.categoria}
                sm={6}
                md={4}
              />
              <Grid item xs={12} sm={6} md={4}>
                <Typography className={classes.fieldLabel}>
                  Preço na loja
                </Typography>
                <Box className={classes.priceHighlight}>
                  {formatBRL(coleta.precoMarcaLoja ?? coleta.precoKarstenLoja)}
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider style={{ marginBottom: 20 }} />

          <Box className={classes.section} style={{ marginBottom: 0 }}>
            <SectionHeader
              classes={classes}
              icon={CompareArrowsIcon}
              title="Concorrentes"
            />
            {concorrentes.length ? (
              <Box className={classes.concorrentesGrid}>
                {concorrentes.map((c, idx) => (
                  <ConcorrenteCard
                    key={`${coleta.idColeta}-c-${c.idProdutoConcorrente ?? idx}`}
                    classes={classes}
                    concorrente={c}
                    loadingFoto={loadingFotoId}
                    onViewFoto={handleViewFoto}
                  />
                ))}
              </Box>
            ) : (
              <Typography className={classes.emptyConcorrentes}>
                Nenhum concorrente registrado nesta coleta.
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions style={{ padding: "12px 20px" }}>
          <Button onClick={handleCloseMain} color="primary" variant="contained">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <DialogFotoConcorrente
        open={fotoModal.open}
        onClose={handleCloseFotoModal}
        title={fotoModal.title}
        loading={fotoModal.loading}
        src={fotoModal.src}
        error={fotoModal.error}
      />
    </>
  );
}

DialogDetalheColeta.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  coleta: PropTypes.object,
};

DetailField.propTypes = {
  classes: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  xs: PropTypes.number,
  sm: PropTypes.number,
  md: PropTypes.number,
};

SectionHeader.propTypes = {
  classes: PropTypes.object.isRequired,
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
};

ConcorrenteCard.propTypes = {
  classes: PropTypes.object.isRequired,
  concorrente: PropTypes.object.isRequired,
  loadingFoto: PropTypes.number,
  onViewFoto: PropTypes.func.isRequired,
};

DialogFotoConcorrente.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  loading: PropTypes.bool,
  src: PropTypes.string,
  error: PropTypes.string,
};
